const GIFT_PROPERTY = '_free_gift';
const ADD_SOURCES = new Set(['add', 'form', 'product-form', 'quick-view', 'upsell', 'bundle']);
const STEP_DELAY = 380;
const TYPE_DELAY = 700;
const recommendationCache = new Map();
let lastRestoreRefresh = -1;

const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

const isGift = (item) => {
  const value = item?.properties?.[GIFT_PROPERTY];
  return value !== undefined && value !== null && value !== '';
};

const sameProperties = (a, b) => JSON.stringify(a?.properties || {}) === JSON.stringify(b?.properties || {});

// Wallets switched off in the cart section's Cart settings are dropped from Shopify's wallet list (`wallet-configs`)
// before the buttons draw; the buttons themselves sit in a closed shadow root and can't be removed after.
function filterWallets(node) {
  const hidden = (node.closest('[data-hidden-wallets]')?.dataset.hiddenWallets || '').split(',').filter(Boolean);
  const raw = node.getAttribute('wallet-configs');
  if (!hidden.length || !raw) return;
  let configs;
  try {
    configs = JSON.parse(raw);
  } catch {
    return;
  }
  if (!Array.isArray(configs)) return;
  const kept = configs.filter((config) => !hidden.includes(config?.name));
  if (!kept.length || kept.length === configs.length) return;
  node.setAttribute('wallet-configs', JSON.stringify(kept));
  [...node.querySelectorAll('.wallet-cart-button-container')].slice(kept.length).forEach((cell) => cell.remove());
}

function sizedImage(url, width) {
  if (!url) return '';
  const src = String(url);
  if (/[?&]width=/.test(src)) return src;
  return `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
}

class CartItems extends HTMLElement {
  get innerSelector() {
    return '[data-cart-inner]';
  }

  get source() {
    return 'cart';
  }

  get sectionUrl() {
    return Theme.routes?.root || '/';
  }

  connectedCallback() {
    this.inner = this.querySelector(this.innerSelector);
    this.live = this.querySelector('[data-cart-live]');
    if (!this.inner) return;

    this.sectionId = this.dataset.sectionId;
    window.Theme?.cart?.registerSection?.(this.sectionId);
    this.pending = new Map();
    this.inflight = new Map();
    this.latest = new Map();
    this.requests = new Map();
    this.timers = new Map();
    this.lineErrors = new Map();
    this.globalError = '';
    this.refreshPromise = null;
    this.refreshQueued = false;
    this.lastSource = '';
    this.giftProducts = new Set((this.dataset.giftProducts || '').split(',').filter(Boolean));

    this.onUpdated = (event) => this.handleUpdated(event);
    document.addEventListener('cart:updated', this.onUpdated);
    this.onGiftsBusy = () => this.updateExpressGate();
    document.addEventListener('gifts:busy', this.onGiftsBusy);
    this.onPageShow = (event) => this.handlePageShow(event);
    window.addEventListener('pageshow', this.onPageShow);

    this.addEventListener('click', (event) => this.handleClick(event));
    this.addEventListener('change', (event) => this.handleChange(event));
    this.addEventListener('input', (event) => this.handleInput(event));
    this.addEventListener('keydown', (event) => this.handleKeydown(event));
    this.addEventListener('submit', (event) => this.handleSubmit(event));
    this.addEventListener('invalid', (event) => this.handleInvalid(event), true);
    this.addEventListener('toggle', (event) => {
      if (event.target.matches?.('[data-cart-note-details]')) this.noteOpen = event.target.open;
    }, true);
    this.upsellObserver = new ResizeObserver((entries) => entries.forEach((entry) => this.syncUpsellArrows(entry.target)));
    this.addEventListener('scroll', (event) => {
      if (event.target.matches?.('[data-upsells-list]')) this.syncUpsellArrows(event.target);
    }, { capture: true, passive: true });
    this.addEventListener('pointerdown', (event) => this.dragUpsells(event));
    this.addEventListener('dragstart', (event) => {
      if (event.target.closest?.('[data-upsells-list]')) event.preventDefault();
    });

    this.enhance(this.inner);
  }

  disconnectedCallback() {
    window.Theme?.cart?.unregisterSection?.(this.sectionId);
    document.removeEventListener('cart:updated', this.onUpdated);
    document.removeEventListener('gifts:busy', this.onGiftsBusy);
    window.removeEventListener('pageshow', this.onPageShow);
    clearTimeout(this.gateTimer);
    this.upsellObserver?.disconnect();
  }

  syncUpsellArrows(list) {
    const box = list.closest('.cart-upsells');
    if (!box || list.hidden) return;
    const max = list.scrollWidth - list.clientWidth;
    box.toggleAttribute('data-overflow', max > 1);
    box.toggleAttribute('data-at-start', list.scrollLeft <= 1);
    box.toggleAttribute('data-at-end', list.scrollLeft >= max - 1);
  }

  selectUpsellTab(tab) {
    const box = tab?.closest('.cart-upsells');
    if (!box) return;
    box.querySelectorAll('[data-upsells-tab]').forEach((other) => {
      const selected = other === tab;
      other.setAttribute('aria-selected', String(selected));
      const panel = box.querySelector(`#${CSS.escape(other.getAttribute('aria-controls'))}`);
      if (panel) panel.hidden = !selected;
    });
    const list = box.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);
    if (list) {
      list.scrollLeft = 0;
      this.syncUpsellArrows(list);
    }
  }

  // Touch and trackpads scroll the row natively; a mouse drags it.
  dragUpsells(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const list = event.target.closest('[data-upsells-list]');
    if (!list || list.scrollWidth - list.clientWidth <= 1) return;
    const startX = event.clientX;
    const startLeft = list.scrollLeft;
    let dragging = false;
    const move = (moveEvent) => {
      const dx = moveEvent.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) < 6) return;
        dragging = true;
        list.classList.add('is-dragging');
      }
      list.scrollLeft = startLeft - dx;
    };
    const end = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      if (!dragging) return;
      list.classList.remove('is-dragging');
      const swallow = (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
      };
      list.addEventListener('click', swallow, { capture: true, once: true });
      setTimeout(() => list.removeEventListener('click', swallow, true), 0);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  }

  // Back from checkout restores the page from bfcache with the submit spinner still on.
  handlePageShow(event) {
    this.checkingOut = false;
    this.querySelectorAll('.cart-drawer__checkout[aria-busy="true"]').forEach((button) => button.removeAttribute('aria-busy'));
    this.updateExpressGate();
    if (event.persisted && lastRestoreRefresh !== event.timeStamp) {
      lastRestoreRefresh = event.timeStamp;
      Theme.cart.refresh?.('refresh').catch(() => {});
    }
  }

  isBusy() {
    return Boolean(this.inflight.size || this.timers.size || this.refreshPromise || window.ConvertGifts?.isBusy?.());
  }

  termsBlocked(root = this.inner) {
    const terms = root?.querySelector('[data-cart-terms]');
    return Boolean(terms && !terms.checked);
  }

  // Express buttons (Shop Pay, Apple Pay, PayPal iframes) skip the checkout form, so they stay
  // inert behind a gate until terms are accepted and quantity edits and gift reconciliation settle.
  updateExpressGate() {
    clearTimeout(this.gateTimer);
    const express = this.inner?.querySelector('[data-cart-express]');
    if (!express) return;
    const buttons = express.querySelector('[data-cart-express-buttons]');
    const gate = express.querySelector('[data-cart-express-gate]');
    const reason = this.termsBlocked() ? 'terms' : this.isBusy() ? 'busy' : '';
    express.dataset.gate = reason;
    express.toggleAttribute('aria-busy', reason === 'busy');
    if (buttons) buttons.inert = Boolean(reason);
    if (gate) {
      gate.hidden = !reason;
      const label = reason === 'terms' ? this.inner.querySelector('[data-cart-terms-error]')?.textContent.trim() : express.dataset.busyLabel;
      gate.setAttribute('aria-label', label || '');
      const text = gate.querySelector('[data-cart-express-gate-label]');
      if (text) text.textContent = label || '';
    }
    if (reason === 'busy') this.gateTimer = setTimeout(() => this.updateExpressGate(), 400);
  }

  handleExpressGate() {
    const express = this.inner?.querySelector('[data-cart-express]');
    if (express?.dataset.gate === 'terms') {
      this.toggleTermsError(true);
      this.inner.querySelector('[data-cart-terms]')?.focus();
      return;
    }
    this.flushPending()
      .then(() => window.ConvertGifts?.idle?.())
      .catch(() => {})
      .then(() => this.updateExpressGate());
  }

  cartFrom(root = this.inner) {
    const node = root?.querySelector('[data-cart-json]');
    if (!node) return Theme.cart?.state || null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return Theme.cart?.state || null;
    }
  }

  handleUpdated(event) {
    const { cart, source, openDrawer, sections } = event.detail || {};
    if (!cart || source === 'note') return;
    this.lastSource = source || '';
    const wantOpen =
      Boolean(openDrawer) &&
      Theme.settings?.cartOpenOnAdd !== false &&
      ADD_SOURCES.has(source) &&
      typeof this.open === 'function' &&
      !document.querySelector('cart-page');
    if (wantOpen && !this.dialog?.open) {
      this.dialog?.classList.add('is-adding');
      this.open({ afterAdd: true });
    }
    this.refresh(sections?.[this.sectionId]).finally(() => this.dialog?.classList.remove('is-adding'));
  }

  // `html` is the section as rendered by the cart write itself; without it the section is fetched.
  refresh(html = null) {
    this.nextHtml = html || null;
    if (this.refreshPromise) {
      this.refreshQueued = true;
      return this.refreshPromise;
    }
    this.setLoading(true);
    this.refreshPromise = (async () => {
      do {
        this.refreshQueued = false;
        const ready = this.nextHtml;
        this.nextHtml = null;
        await this.renderOnce(ready);
      } while (this.refreshQueued);
    })()
      .catch((error) => console.error(error))
      .finally(() => {
        this.refreshPromise = null;
        this.setLoading(false);
        this.updateExpressGate();
      });
    return this.refreshPromise;
  }

  setLoading(loading) {
    this.classList.toggle('is-loading', loading);
  }

  async renderOnce(ready = null) {
    if (!this.sectionId) return;
    let html = ready;
    if (!html) {
      const url = new URL(this.sectionUrl, window.location.origin);
      url.searchParams.set('sections', this.sectionId);
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Cart section ${this.sectionId} failed to load`);
      const payload = await response.json();
      html = payload[this.sectionId];
    }
    if (!html) return;
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector(this.innerSelector);
    if (!next) return;
    this.swap(next);
  }

  swap(next) {
    const snapshot = this.snapshot();
    this.reuseImages(next);
    if (!this.updateAroundExpressButtons(next)) {
      this.keepExpressButtons(next);
      this.inner.replaceWith(next);
      this.inner = next;
    }
    this.enhance(this.inner);
    this.restore(snapshot);
    this.announce();
  }

  // A fresh <img> lazy-loads and decodes again even when cached, so every photo would blink on each update;
  // photos that are still shown keep their loaded element (and its auto-fit) instead.
  reuseImages(next) {
    const keyOf = (img) => `${img.getAttribute('src')}|${img.getAttribute('srcset') || ''}`;
    const pool = new Map();
    this.inner?.querySelectorAll('img[src]').forEach((img) => {
      if (!img.complete || !img.naturalWidth) return;
      const key = keyOf(img);
      if (!pool.has(key)) pool.set(key, []);
      pool.get(key).push(img);
    });
    if (!pool.size) return;
    next.querySelectorAll('img[src]').forEach((img) => {
      const old = pool.get(keyOf(img))?.shift();
      if (!old) return;
      const kept = ['is-fit', 'is-loaded', 'is-instant'].filter((name) => old.classList.contains(name));
      old.className = img.className;
      if (kept.length) old.classList.add(...kept);
      old.alt = img.alt;
      const from = old.parentElement;
      const to = img.parentElement;
      if (from?.classList.contains('is-fit') && to && !to.classList.contains('is-fit')) {
        to.classList.add('is-fit');
        to.style.setProperty('--fit-bg', from.style.getPropertyValue('--fit-bg'));
      }
      img.replaceWith(old);
    });
  }

  // Shopify only initializes dynamic checkout buttons present at page load; re-rendered copies stay blank.
  // Buttons that never showed (the empty bag keeps a hidden copy) or that would land somewhere hidden are
  // redrawn from the page-load template once visible, since moving them makes PayPal draw an offset copy.
  keepExpressButtons(next) {
    const live = this.inner?.querySelector('[data-cart-express-buttons]');
    const slot = next.querySelector('[data-cart-express-buttons]');
    if (!live || !slot || live === slot) return;
    const canRedraw = typeof this.queueExpressButtons === 'function';
    if (canRedraw && (live.closest('[hidden]') || slot.closest('[hidden]'))) {
      this.queueExpressButtons(slot);
      return;
    }
    slot.replaceWith(live);
  }

  // Moving the wallet buttons reloads their iframes and PayPal draws a second, offset copy, so when the
  // new render puts them in the same spot, everything around them is replaced and they stay put.
  updateAroundExpressButtons(next) {
    const live = this.inner?.querySelector('[data-cart-express-buttons]');
    const slot = next.querySelector('[data-cart-express-buttons]');
    if (!live || !slot) return false;
    const route = (node, root) => {
      const chain = [];
      for (let el = node; el && el !== root; el = el.parentElement) chain.unshift(el);
      return chain;
    };
    const liveChain = route(live, this.inner);
    const slotChain = route(slot, next);
    if (liveChain.length !== slotChain.length) return false;
    if (liveChain.some((el, i) => el.tagName !== slotChain[i].tagName || el.className !== slotChain[i].className)) return false;

    const syncAttributes = (target, source) => {
      [...target.attributes].forEach(({ name }) => {
        if (!source.hasAttribute(name)) target.removeAttribute(name);
      });
      [...source.attributes].forEach(({ name, value }) => {
        if (target.getAttribute(name) !== value) target.setAttribute(name, value);
      });
    };
    let target = this.inner;
    let source = next;
    for (let depth = 0; depth < liveChain.length; depth += 1) {
      syncAttributes(target, source);
      const keep = liveChain[depth];
      const incoming = slotChain[depth];
      [...target.childNodes].forEach((child) => {
        if (child !== keep) child.remove();
      });
      const before = [];
      let after = false;
      [...source.childNodes].forEach((child) => {
        if (child === incoming) after = true;
        else if (after) target.append(child);
        else before.push(child);
      });
      keep.before(...before);
      target = keep;
      source = incoming;
    }
    return true;
  }

  snapshot() {
    const active = document.activeElement;
    const focusNode = active && this.inner.contains(active) ? active.closest('[data-focus-id]') : null;
    const line = active?.closest?.('[data-cart-line]');
    const lines = [...this.inner.querySelectorAll('[data-cart-line]')];
    return {
      focusId: focusNode?.dataset.focusId || null,
      hadFocus: Boolean(active && this.inner.contains(active)),
      lineIndex: line ? lines.indexOf(line) : -1,
      scrollTop: this.scroller()?.scrollTop || 0,
      upsellScroll: [...this.inner.querySelectorAll('[data-upsells-list]')].map((list) => list.scrollLeft),
      upsellTab: this.inner.querySelector('[data-upsells-tab][aria-selected="true"]')?.getAttribute('aria-controls') || null,
      upsellsOpen: Boolean(this.inner.querySelector('[data-cart-upsells-details]')?.open),
      termsChecked: Boolean(this.inner.querySelector('[data-cart-terms]')?.checked),
    };
  }

  scroller(root = this.inner) {
    return root.querySelector('[data-cart-drawer-body]') || null;
  }

  restore(snapshot) {
    const scroller = this.scroller();
    if (snapshot.upsellsOpen) this.inner.querySelector('[data-cart-upsells-details]')?.setAttribute('open', '');
    const terms = this.inner.querySelector('[data-cart-terms]');
    if (terms && snapshot.termsChecked) terms.checked = true;
    this.updateExpressGate();
    if (scroller) scroller.scrollTop = snapshot.scrollTop;
    const tab = snapshot.upsellTab && this.inner.querySelector(`[data-upsells-tab][aria-controls="${CSS.escape(snapshot.upsellTab)}"]:not([hidden])`);
    if (tab && tab.getAttribute('aria-selected') !== 'true') this.selectUpsellTab(tab);
    this.inner.querySelectorAll('[data-upsells-list]').forEach((list, index) => {
      if (snapshot.upsellScroll[index]) list.scrollLeft = snapshot.upsellScroll[index];
    });
    if (!snapshot.hadFocus) return;
    let target = snapshot.focusId ? this.inner.querySelector(`[data-focus-id="${CSS.escape(snapshot.focusId)}"]`) : null;
    if (target?.disabled) target = target.closest('[data-cart-line]')?.querySelector('[data-line-qty]') || null;
    if (!target && snapshot.lineIndex > -1) {
      const lines = [...this.inner.querySelectorAll('[data-cart-line]:not([data-gift-line])')];
      const line = lines[Math.min(snapshot.lineIndex, lines.length - 1)];
      target = line?.querySelector('[data-line-remove], [data-line-qty]') || null;
    }
    target = target || this.inner.querySelector('[data-focus-id="close"]') || this.inner.querySelector('a[href], button');
    target?.focus({ preventScroll: true });
  }

  enhance(root) {
    root.querySelectorAll('shopify-accelerated-checkout-cart').forEach(filterWallets);
    root.querySelectorAll('[data-cart-line]').forEach((line) => {
      const key = line.dataset.lineKey;
      const input = line.querySelector('[data-line-qty]');
      // A render can land between two requests for the same line and show an older quantity;
      // keep the last value the shopper asked for until a render confirms it.
      const wanted = this.pending.has(key) ? this.pending.get(key) : this.latest.get(key);
      if (input && wanted !== undefined) {
        if (!this.pending.has(key) && !this.inflight.has(key) && Number(line.dataset.quantity) === wanted) {
          this.latest.delete(key);
        } else {
          input.value = String(wanted);
          this.syncStepper(line);
        }
      }
      if (this.inflight.has(key)) line.setAttribute('aria-busy', 'true');
      const error = this.lineErrors.get(key);
      const slot = line.querySelector('[data-line-error]');
      if (slot && error) {
        slot.textContent = error;
        slot.hidden = false;
      }
    });
    this.renderGlobalError(root);
    this.upsellObserver.disconnect();
    root.querySelectorAll('[data-upsells-list]').forEach((list) => this.upsellObserver.observe(list));
    if (root === this.inner) this.updateExpressGate();
    const details = root.querySelector('[data-cart-note-details]');
    if (details && this.noteOpen !== undefined) details.open = this.noteOpen || details.open;
    this.loadRecommendations(root);
    document.dispatchEvent(new CustomEvent('cart:rendered', { detail: { cart: this.cartFrom(root), root, source: this.source } }));
  }

  announce() {
    if (!this.live) return;
    const source = this.lastSource;
    if (!source || source === 'gifts' || source === 'refresh') return;
    const { addedMessage, updatedMessage, removedMessage } = this.live.dataset;
    const message = ADD_SOURCES.has(source) ? addedMessage : this.removedLine && removedMessage ? removedMessage : updatedMessage;
    this.removedLine = false;
    this.live.textContent = '';
    requestAnimationFrame(() => (this.live.textContent = message || ''));
    this.lastSource = '';
  }

  handleClick(event) {
    if (event.target.closest('[data-cart-express-gate]')) {
      event.preventDefault();
      this.handleExpressGate();
      return;
    }
    const step = event.target.closest('[data-line-step]');
    if (step) {
      event.preventDefault();
      this.step(step.closest('[data-cart-line]'), Number(step.dataset.lineStep));
      return;
    }
    const remove = event.target.closest('[data-line-remove]');
    if (remove) {
      event.preventDefault();
      const line = remove.closest('[data-cart-line]');
      if (!line) return;
      line.classList.add('is-removing');
      this.schedule(line, 0, 0);
      return;
    }
    const upsell = event.target.closest('[data-upsell-add]');
    if (upsell) {
      event.preventDefault();
      this.addUpsell(upsell);
      return;
    }
    const tab = event.target.closest('[data-upsells-tab]');
    if (tab) {
      this.selectUpsellTab(tab);
      return;
    }
    const arrow = event.target.closest('[data-upsells-prev], [data-upsells-next]');
    if (arrow) {
      const list = arrow.closest('[data-cart-upsells], .cart-upsells')?.querySelector('[data-upsells-list]:not([hidden])');
      if (!list) return;
      const direction = arrow.hasAttribute('data-upsells-prev') ? -1 : 1;
      list.scrollBy({ left: direction * list.clientWidth * 0.85, behavior: 'smooth' });
    }
  }

  handleChange(event) {
    const input = event.target.closest('[data-line-qty]');
    if (input) {
      const line = input.closest('[data-cart-line]');
      if (input.value === '') return;
      this.schedule(line, Number(input.value), 0);
      return;
    }
    const terms = event.target.closest('[data-cart-terms]');
    if (terms) {
      if (terms.checked) this.toggleTermsError(false);
      this.updateExpressGate();
    }
  }

  handleInput(event) {
    const input = event.target.closest('[data-line-qty]');
    if (input) {
      if (input.value === '') return;
      this.schedule(input.closest('[data-cart-line]'), Number(input.value), TYPE_DELAY);
      return;
    }
    const note = event.target.closest('[data-cart-note]');
    if (note) {
      clearTimeout(this.noteTimer);
      this.noteTimer = setTimeout(() => {
        this.noteSave = Theme.cart.update({}, { note: note.value, source: 'note', toast: false }).catch(() => {});
      }, 500);
    }
  }

  handleKeydown(event) {
    const input = event.target.closest?.('[data-line-qty]');
    if (input && event.key === 'Enter') {
      event.preventDefault();
      if (input.value !== '') this.schedule(input.closest('[data-cart-line]'), Number(input.value), 0);
    }
  }

  handleSubmit(event) {
    const form = event.target.closest('[data-cart-checkout]');
    if (!form) return;
    const terms = [...form.elements].find((el) => el.matches('[data-cart-terms]'));
    if (terms && !terms.checked) {
      event.preventDefault();
      this.toggleTermsError(true);
      terms.focus();
      return;
    }
    Theme.ss?.('checkout_click', 'checkout_click', { via: 'cart', at: this.dialog ? 'drawer' : 'cart' });
    const gifts = window.ConvertGifts;
    const direct = event.submitter?.name === 'checkout';
    if (direct || this.inflight.size || this.timers.size || gifts?.isBusy?.()) {
      event.preventDefault();
      if (this.checkingOut) return;
      this.checkingOut = true;
      event.submitter?.setAttribute('aria-busy', 'true');
      const formId = form.id;
      this.flushPending()
        .then(() => gifts?.idle?.())
        .then(() => this.refreshPromise)
        .then(() => (direct ? this.saveNote() : null))
        .catch(() => {})
        .then(() => {
          this.checkingOut = false;
          if (direct) {
            window.location.assign(this.checkoutUrl());
            return;
          }
          const target = (formId && document.getElementById(formId)) || this.querySelector('form[data-cart-checkout]');
          if (!target) return;
          const checkout = document.createElement('input');
          checkout.type = 'hidden';
          checkout.name = 'checkout';
          target.append(checkout);
          target.submit();
        });
      return;
    }
    event.submitter?.setAttribute('aria-busy', 'true');
  }

  // Quantities and the note are saved over AJAX, so Checkout can open /checkout directly instead of
  // posting the form to /cart and following its redirect: one round trip fewer before checkout starts.
  checkoutUrl() {
    return `${(window.Theme?.routes?.root || '/').replace(/\/$/, '')}/checkout`;
  }

  saveNote() {
    clearTimeout(this.noteTimer);
    const note = this.inner?.querySelector('[data-cart-note]');
    const saved = Theme.cart?.state?.note ?? this.cartFrom()?.note ?? '';
    if (note && note.value !== saved) {
      this.noteSave = Theme.cart.update({}, { note: note.value, source: 'note', toast: false }).catch(() => {});
    }
    return this.noteSave;
  }

  handleInvalid(event) {
    if (!event.target.matches?.('[data-cart-terms]')) return;
    event.preventDefault();
    this.toggleTermsError(true);
    event.target.focus();
  }

  toggleTermsError(show) {
    const error = this.inner.querySelector('[data-cart-terms-error]');
    const input = this.inner.querySelector('[data-cart-terms]');
    if (error) error.hidden = !show;
    input?.setAttribute('aria-invalid', String(show));
  }

  maxFor(line) {
    const max = Number(line?.querySelector('[data-line-qty]')?.getAttribute('max'));
    return Number.isFinite(max) && max > 0 ? max : Infinity;
  }

  step(line, delta) {
    if (!line) return;
    const input = line.querySelector('[data-line-qty]');
    if (!input) return;
    const key = line.dataset.lineKey;
    const current = this.pending.has(key) ? this.pending.get(key) : Number(input.value || 0);
    const max = this.maxFor(line);
    if (delta > 0 && current >= max) {
      this.setLineError(line, this.limitMessage(max));
      return;
    }
    const next = Math.max(0, Math.min(max, current + delta));
    input.value = String(next);
    this.schedule(line, next, next === 0 ? 0 : STEP_DELAY);
  }

  schedule(line, quantity, delay) {
    if (!line || !Number.isFinite(quantity)) return;
    const key = line.dataset.lineKey;
    const max = this.maxFor(line);
    let target = Math.max(0, Math.floor(quantity));
    if (target > max) {
      target = max;
      this.setLineError(line, this.limitMessage(max));
    } else {
      this.setLineError(line, '');
    }
    const input = line.querySelector('[data-line-qty]');
    if (input && Number(input.value) !== target) input.value = String(target);
    this.pending.set(key, target);
    this.latest.set(key, target);
    this.syncStepper(line);
    clearTimeout(this.timers.get(key));
    this.timers.set(
      key,
      setTimeout(() => {
        this.timers.delete(key);
        this.commit(key);
      }, delay)
    );
    this.updateExpressGate();
  }

  syncStepper(line) {
    const input = line.querySelector('[data-line-qty]');
    const plus = line.querySelector('[data-line-step="1"]');
    if (!input || !plus) return;
    plus.disabled = Number(input.value) >= this.maxFor(line);
  }

  commit(key) {
    if (!this.pending.has(key)) return Promise.resolve();
    const quantity = this.pending.get(key);
    const line = this.lineByKey(key);
    if (line && Number(line.dataset.quantity) === quantity && !this.inflight.has(key)) {
      this.pending.delete(key);
      this.latest.delete(key);
      line.classList.remove('is-removing');
      return Promise.resolve();
    }
    this.inflight.set(key, (this.inflight.get(key) || 0) + 1);
    if (quantity === 0) this.removedLine = true;
    this.lineByKey(key)?.setAttribute('aria-busy', 'true');
    const groupKeys = [...new Set((line?.dataset.lineKeys || '').split(',').filter(Boolean))];
    const write =
      groupKeys.length > 1
        ? Theme.cart.update(Object.fromEntries(groupKeys.map((groupKey) => [groupKey, groupKey === key ? quantity : 0])), {
            source: this.source,
            toast: false,
          })
        : Theme.cart.change({ id: key, quantity }, { source: this.source, toast: false });
    const request = write
      .then((cart) => {
        if (this.pending.get(key) === quantity) this.pending.delete(key);
        const updated = cart?.items?.find((item) => item.key === key);
        // Shopify may split the merged line again, so a group's confirmed quantity is the variant's paid total.
        const confirmed =
          groupKeys.length > 1 && updated
            ? cart.items
                .filter((item) => item.variant_id === updated.variant_id && sameProperties(item, updated))
                .reduce((sum, item) => sum + item.quantity, 0)
            : updated?.quantity ?? 0;
        if (!this.pending.has(key) && this.inflight.get(key) === 1) this.latest.set(key, confirmed);
        if (quantity > 0 && updated && confirmed < quantity) {
          this.lineErrors.set(key, this.limitMessage(confirmed));
        } else {
          this.lineErrors.delete(key);
        }
      })
      .catch((error) => {
        if (this.pending.get(key) === quantity) this.pending.delete(key);
        this.latest.delete(key);
        this.lineErrors.set(key, error?.message || Theme.strings?.error || '');
        this.refresh();
      })
      .finally(() => {
        const count = (this.inflight.get(key) || 1) - 1;
        if (count > 0) this.inflight.set(key, count);
        else this.inflight.delete(key);
        const current = this.lineByKey(key);
        if (!count) current?.removeAttribute('aria-busy');
        current?.classList.remove('is-removing');
        const message = this.lineErrors.get(key);
        if (current && message) this.setLineError(current, message);
        if (this.requests.get(key) === request) this.requests.delete(key);
        this.updateExpressGate();
      });
    this.requests.set(key, request);
    return request;
  }

  flushPending() {
    const keys = [...this.timers.keys()];
    keys.forEach((key) => clearTimeout(this.timers.get(key)));
    this.timers.clear();
    const running = [...this.requests.values()];
    return Promise.all([...running, ...keys.map((key) => this.commit(key))]).then(() => this.refreshPromise);
  }

  lineByKey(key) {
    return this.inner.querySelector(`[data-cart-line][data-line-key="${CSS.escape(key)}"]`);
  }

  limitMessage(quantity) {
    const template = this.dataset.limitMessage || '';
    return template.replace('[quantity]', String(quantity));
  }

  setLineError(line, message) {
    const key = line.dataset.lineKey;
    const slot = line.querySelector('[data-line-error]');
    if (message) this.lineErrors.set(key, message);
    else this.lineErrors.delete(key);
    if (!slot) return;
    slot.textContent = message;
    slot.hidden = !message;
  }

  showError(message) {
    this.globalError = message || '';
    clearTimeout(this.errorTimer);
    if (message) {
      this.errorTimer = setTimeout(() => {
        this.globalError = '';
        this.renderGlobalError(this.inner);
      }, 7000);
    }
    this.renderGlobalError(this.inner);
  }

  renderGlobalError(root) {
    const slot = root?.querySelector('[data-cart-error]');
    if (!slot) return;
    slot.textContent = this.globalError;
    slot.hidden = !this.globalError;
  }

  async addUpsell(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    this.showError('');
    try {
      await Theme.cart.add([{ id: Number(button.dataset.upsellAdd), quantity: 1 }], { source: 'upsell', toast: false });
      Theme.ss?.('feat', 'upsell_add', { variant: Number(button.dataset.upsellAdd), at: this.dialog ? 'drawer' : 'cart' });
    } catch (error) {
      this.showError(error?.message || Theme.strings?.error || '');
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  excludedProducts(root) {
    const cart = this.cartFrom(root);
    const excluded = new Set(this.giftProducts);
    (cart?.items || []).forEach((item) => excluded.add(String(item.product_id)));
    return excluded;
  }

  fetchRecommendations(productId, limit) {
    const key = `${productId}:${limit}`;
    if (!recommendationCache.has(key)) {
      const load = async (intent) => {
        try {
          const url = `${Theme.routes.recommendations}.json?product_id=${encodeURIComponent(productId)}&limit=${limit}&intent=${intent}`;
          const data = await Theme.fetchJSON(url);
          return Array.isArray(data.products) ? data.products : [];
        } catch (error) {
          return [];
        }
      };
      const request = Promise.all([load('related'), load('complementary')]).then(([related, complementary]) => {
        const seen = new Set(related.map((product) => product.id));
        return [...related, ...complementary.filter((product) => !seen.has(product.id))];
      });
      recommendationCache.set(key, request);
    }
    return recommendationCache.get(key);
  }

  // Related, then complementary recommendations go ahead of the server-rendered fallbacks
  // (anchor collection, fallback collection, best sellers); under 2 products hides the block.
  async loadRecommendations(root) {
    const mount = root.querySelector('[data-cart-upsells-fetch]');
    if (!mount || Theme.abOff?.('upsells')) return;
    const block = mount.closest('[data-complete-fit]') || mount;
    const list = mount.querySelector('[data-upsells-list]');
    if (!list) return;
    const limit = Number(mount.dataset.limit || 4);
    const excluded = this.excludedProducts(root);
    const products = mount.dataset.productId ? await this.fetchRecommendations(mount.dataset.productId, limit + excluded.size) : [];
    if (!mount.isConnected) return;
    const seen = new Set(excluded);
    const cards = [];
    for (const product of products) {
      const id = String(product.id);
      if (product.available === false || seen.has(id)) continue;
      seen.add(id);
      cards.push(this.renderUpsellCard(product, mount.dataset));
    }
    for (const card of list.querySelectorAll('[data-product-id]')) {
      if (seen.has(card.dataset.productId)) continue;
      seen.add(card.dataset.productId);
      cards.push(card);
    }
    const shown = cards.slice(0, limit);
    list.removeAttribute('aria-busy');
    list.replaceChildren(...shown);
    const tabs = [...mount.querySelectorAll('[data-upsells-tab]')];
    if (tabs.length > 1) {
      // With collection tabs only the main tab drops out when recommendations come up short.
      const main = tabs.find((tab) => tab.getAttribute('aria-controls') === list.id);
      if (main) main.hidden = shown.length < 2;
      if (main?.hidden && main.getAttribute('aria-selected') === 'true') this.selectUpsellTab(tabs.find((tab) => !tab.hidden));
    } else {
      block.hidden = shown.length < 2;
    }
    this.syncUpsellArrows(list);
  }

  renderUpsellCard(product, strings) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const variant = variants.find((item) => item.available) || variants[0] || {};
    const title = escapeHtml(product.title);
    const url = escapeHtml(product.url);
    const image = product.featured_image
      ? `<img src="${escapeHtml(sizedImage(product.featured_image, 480))}" alt="" loading="lazy" width="160" height="200">`
      : '';
    const price = Number(variant.price ?? product.price ?? 0);
    const compare = Number(variant.compare_at_price ?? product.compare_at_price ?? 0);
    const priceHtml =
      compare > price
        ? `<s class="text-muted">${Theme.formatMoney(compare)}</s><span class="text-sale">${Theme.formatMoney(price)}</span>`
        : Theme.formatMoney(price);
    const direct = variants.length === 1 && variant.available && !product.requires_selling_plan;
    const icon = this.querySelector('[data-icon-plus]')?.innerHTML || '';
    const label = (direct ? strings.addLabel : strings.chooseLabel || '').replace('[title]', product.title);
    const action = direct ? `data-upsell-add="${escapeHtml(variant.id)}"` : `data-quick-view="${url}" aria-haspopup="dialog"`;
    const li = document.createElement('li');
    li.className = 'cart-upsells__card';
    li.dataset.productId = product.id;
    li.innerHTML = `
      <a class="cart-upsells__media media media--product" href="${url}" data-fit-max="3" tabindex="-1" aria-hidden="true">${image}</a>
      <div class="cart-upsells__info">
        <a class="cart-upsells__title" href="${url}">${title}</a>
        <span class="cart-upsells__price">${priceHtml}</span>
      </div>
      <button type="button" class="cart-upsells__add" ${action} aria-label="${escapeHtml(label)}">${icon}<span>${escapeHtml(strings.addText || '')}</span></button>`;
    return li;
  }
}

class CartDrawer extends CartItems {
  get innerSelector() {
    return '[data-cart-drawer-inner]';
  }

  get source() {
    return 'drawer';
  }

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;
    super.connectedCallback();
    this.focusReturn = null;

    this.onOpen = () => this.open();
    this.onClose = () => this.close();
    document.addEventListener('cart:open', this.onOpen);
    document.addEventListener('cart:close', this.onClose);
    this.deferExpressButtons();
    if (window.Shopify?.designMode) {
      this.onSectionSelect = (event) => {
        if (event.target.contains(this)) this.open();
      };
      this.onSectionDeselect = (event) => {
        if (event.target.contains(this)) this.close();
      };
      this.onBlockSelect = (event) => {
        if (this.contains(event.target)) this.open();
      };
      document.addEventListener('shopify:section:select', this.onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    // The press has to start on the backdrop too, so a drag that ends outside (an upsell swipe, a text selection) keeps the bag open.
    let pressedBackdrop = false;
    this.dialog.addEventListener('pointerdown', (event) => {
      pressedBackdrop = event.target === this.dialog;
    });
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog && pressedBackdrop) Theme.cart.close();
      pressedBackdrop = false;
    });
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      Theme.cart.close();
    });
    this.dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('cart-drawer-open');
      Theme.lockScroll(false);
      const target = this.focusReturn;
      this.focusReturn = null;
      if (target?.isConnected && target.focus) target.focus({ preventScroll: true });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart:open', this.onOpen);
    document.removeEventListener('cart:close', this.onClose);
    if (this.onSectionSelect) {
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }
  }

  setLoading(loading) {
    this.dialog?.classList.toggle('is-loading', loading);
  }

  // PayPal renders a second, offset copy when it first draws inside the closed (display: none) drawer,
  // so the wallet buttons are only inserted once the drawer is visible.
  deferExpressButtons() {
    if (this.dialog.open) return;
    this.deferredExpress = [...this.dialog.querySelectorAll('shopify-accelerated-checkout-cart')].map((node) => this.expressMarker(node));
  }

  expressMarker(node) {
    filterWallets(node);
    if (!this.expressTemplate) {
      this.expressTemplate = document.createElement('template');
      this.expressTemplate.innerHTML = node.outerHTML;
      // Shopify may already have drawn the wallets: each is a slotted child that gets its cart data from
      // setDataSource(), which a markup copy can't carry, so a copied button never responds to taps.
      this.expressTemplate.content.querySelectorAll('[slot], style').forEach((child) => child.remove());
    }
    const marker = document.createComment('express-checkout');
    node.replaceWith(marker);
    return { marker, template: this.expressTemplate };
  }

  queueExpressButtons(slot) {
    const queued = [...slot.querySelectorAll('shopify-accelerated-checkout-cart')].map((node) => this.expressMarker(node));
    this.deferredExpress = [...(this.deferredExpress || []).filter(({ marker }) => marker.isConnected), ...queued];
    if (this.dialog.open) this.restoreExpressButtons();
  }

  restoreExpressButtons() {
    if (!this.deferredExpress?.length) return;
    const insert = () => {
      const waiting = [];
      (this.deferredExpress || []).forEach((entry) => {
        const { marker, template } = entry;
        if (!marker.isConnected) return;
        if (!this.dialog.open || marker.parentElement?.closest('[hidden]')) waiting.push(entry);
        else marker.replaceWith(document.importNode(template.content, true));
      });
      this.deferredExpress = waiting.length ? waiting : null;
    };
    const run = () => {
      if (this.refreshPromise) this.refreshPromise.then(insert);
      else insert();
    };
    // Starting the wallet iframes mid slide-in stutters the drawer, so they wait for it to settle.
    requestAnimationFrame(() => {
      const sliding = this.dialog.getAnimations?.() || [];
      if (!sliding.length) run();
      else Promise.all(sliding.map((animation) => animation.finished.catch(() => {}))).then(run);
    });
  }

  handleClick(event) {
    if (event.target.closest('[data-drawer-close]')) {
      event.preventDefault();
      Theme.cart.close();
      return;
    }
    super.handleClick(event);
  }

  open({ afterAdd = false } = {}) {
    if (document.querySelector('cart-page')) return;
    if (this.dialog.open) return;
    const active = document.activeElement;
    this.focusReturn = active && active !== document.body ? active : null;
    this.dialog.showModal();
    this.restoreExpressButtons();
    document.documentElement.classList.add('cart-drawer-open');
    const cart = this.cartFrom?.() || Theme.cart.state;
    if (!afterAdd) Theme.cart.sync?.(cart).catch(() => {});
    Theme.track?.('cart_drawer_opened', {
      itemCount: cart?.item_count ?? null,
      totalPrice: cart?.total_price ?? null,
      currency: cart?.currency?.iso_code || cart?.currency || null,
      trigger: afterAdd ? 'add_to_cart' : 'click',
    });
    Theme.ss?.('cart_open', 'cart_open', { via: afterAdd ? 'add' : 'click', items: (Theme.cart.state || cart)?.item_count ?? null });
    Theme.lockScroll(true);
    const scroller = this.scroller();
    if (scroller && afterAdd) scroller.scrollTop = 0;
    this.inner.querySelector('[data-focus-id="close"]')?.focus({ preventScroll: true });
  }

  close() {
    if (!this.dialog.open) return;
    clearTimeout(this.noteTimer);
    const note = this.inner.querySelector('[data-cart-note]');
    if (note && note.value !== (this.cartFrom()?.note || '')) {
      Theme.cart.update({}, { note: note.value, source: 'note', toast: false }).catch(() => {});
    }
    this.dialog.close();
  }

  scroller(root = this.inner) {
    return root.querySelector('[data-cart-drawer-body]');
  }
}

class CartPage extends CartItems {
  get innerSelector() {
    return '[data-cart-page-inner]';
  }

  get source() {
    return 'cart-page';
  }

  get sectionUrl() {
    return Theme.routes?.cart || '/cart';
  }

  handleUpdated(event) {
    const { source, toast } = event.detail || {};
    super.handleUpdated(event);
    if (toast !== false && ADD_SOURCES.has(source) && source !== 'upsell' && Theme.strings?.added) Theme.toast(Theme.strings.added);
  }

  enhance(root) {
    super.enhance(root);
    this.watchSticky(root);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.stickyObserver?.disconnect();
    this.setSticky(false);
  }

  handleClick(event) {
    if (event.target.closest('[data-cart-sticky-checkout]')) {
      event.preventDefault();
      this.inner.querySelector('.cart-page__summary .cart-drawer__checkout')?.click();
      return;
    }
    super.handleClick(event);
  }

  // Mobile: the summary sits below every line, so a bar mirrors Checkout until the real button scrolls into view.
  watchSticky(root) {
    this.stickyObserver?.disconnect();
    this.setSticky(false);
    this.stickyBar = root.querySelector('[data-cart-sticky]');
    const target = root.querySelector('.cart-page__summary .cart-drawer__checkout');
    if (!this.stickyBar || !target || !('IntersectionObserver' in window)) return;
    this.stickyObserver = new IntersectionObserver(([entry]) => {
      this.setSticky(!entry.isIntersecting && entry.boundingClientRect.top > 0);
    });
    this.stickyObserver.observe(target);
  }

  setSticky(show) {
    const bar = this.stickyBar;
    if (!bar) return;
    const visible = show && window.matchMedia('(max-width: 749px)').matches;
    bar.classList.toggle('is-visible', visible);
    document.body.classList.toggle('has-sticky-atc', visible);
    document.documentElement.style.setProperty('--sticky-atc-height', visible ? `${bar.offsetHeight}px` : '0px');
  }

  scroller() {
    return null;
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
if (!customElements.get('cart-page')) customElements.define('cart-page', CartPage);
